<template>
  <v-form
    ref="rescheduleForm"
    v-model="isFormValid"
    class="d-flex flex-column"
    :disabled="disabled || loading"
    :style="{ gap: '12px' }"
  >
    <p class="ma-0 text-left font-weight-bold">Reprogramar Evento</p>

    <v-menu
      v-model="isRescheduleStartDatePickerOpen"
      :close-on-content-click="false"
      offset-y
    >
      <template #activator="{ on }">
        <v-text-field
          :value="formatDateTime(rescheduleStartDate)"
          label="Fecha y Hora de Inicio"
          filled
          hide-details="auto"
          readonly
          :rules="[rules.required]"
          v-on="on"
        />
      </template>
      <v-date-picker
        v-model="rescheduleStartDate"
        :attributes="[
          {
            key: 'today',
            highlight: { color: 'red', fillMode: 'outline' },
            dates: event.startDate,
          },
        ]"
        color="red"
        is-expanded
        :min-date="minStartDate"
        :minute-increment="5"
        mode="dateTime"
        :model-config="{ type: 'string', mask: 'YYYY-MM-DD HH:mm:ss' }"
      >
        <template #footer>
          <v-divider class="ma-0" />
          <div class="d-flex justify-end pa-2">
            <v-btn
              color="#E20809"
              dark
              :elevation="0"
              small
              @click="isRescheduleStartDatePickerOpen = false"
            >
              OK
            </v-btn>
          </div>
        </template>
      </v-date-picker>
    </v-menu>

    <v-menu
      v-model="isRescheduleEndDatePickerOpen"
      :close-on-content-click="false"
      content-class="white"
      max-width="245"
      offset-y
    >
      <template #activator="{ on }">
        <v-text-field
          :value="formatTime(rescheduleEndDate)"
          label="Hora de Fin"
          :disabled="!rescheduleStartDate"
          filled
          hide-details="auto"
          readonly
          :rules="[rules.required]"
          v-on="on"
        />
      </template>
      <v-date-picker
        v-model="rescheduleEndDate"
        color="red"
        :minute-increment="5"
        mode="time"
        :model-config="{ type: 'string', mask: 'YYYY-MM-DD HH:mm:ss' }"
        :valid-hours="validEndHours"
      >
        <template #footer>
          <v-divider class="ma-0" />
          <div class="d-flex justify-end pa-2">
            <v-btn
              color="#E20809"
              dark
              :elevation="0"
              small
              @click="isRescheduleEndDatePickerOpen = false"
            >
              OK
            </v-btn>
          </div>
        </template>
      </v-date-picker>
    </v-menu>

    <v-combobox
      v-if="availableLocations.length"
      :value="rescheduleLocation"
      label="Ubicación"
      clearable
      filled
      hide-details="auto"
      hide-no-data
      hide-selected
      hint="Escribe o selecciona el lugar del evento"
      :items="availableLocations"
      persistent-hint
      :rules="[rules.required]"
      @change="
        rescheduleLocation = typeof $event === 'object' ? $event.value : $event
      "
      @click:clear="rescheduleLocation = null"
    />
    <v-text-field
      v-else
      v-model="rescheduleLocation"
      label="Ubicación"
      clearable
      filled
      hide-details="auto"
      :rules="[rules.required]"
      @click:clear="rescheduleLocation = null"
    />

    <v-btn
      :color="!isFormValid || disabled ? '' : '#E20809'"
      :dark="isFormValid && !disabled"
      :elevation="0"
      :disabled="!isFormValid || disabled"
      :loading="loading"
      @click="onReschedule"
    >
      Guardar
    </v-btn>
  </v-form>
</template>

<script>
  import DateFormats from '@/core/constants/DateFormats';
  import { isConfirmationWindowExpired } from '@/utilities/schedule/event';
  import moment from 'moment';

  export default {
    name: 'EventDetailsRescheduleForm',
    props: {
      availableLocations: { type: Array, default: () => [] },
      disabled: { type: Boolean, default: false },
      event: { type: Object, required: true },
      loading: { type: Boolean, default: false },
    },
    data: () => ({
      isFormValid: false,
      isInitializing: false,
      isRescheduleStartDatePickerOpen: false,
      isRescheduleEndDatePickerOpen: false,
      rescheduleStartDate: null,
      rescheduleEndDate: null,
      rescheduleLocation: null,
      rules: {
        required: (v) => !!v || 'Requerido',
      },
    }),
    computed: {
      minStartDate() {
        return moment().startOf('day').toDate();
      },
      validEndHours() {
        if (!this.rescheduleStartDate) return null;
        return { min: new Date(this.rescheduleStartDate).getHours() };
      },
    },
    watch: {
      event: {
        immediate: true,
        handler(event) {
          this.isInitializing = true;

          this.resetForm();
          if (!event) {
            this.isInitializing = false;
            return;
          }

          this.rescheduleStartDate = event.startDate;
          this.rescheduleEndDate = event.endDate;
          this.rescheduleLocation = event.location;

          this.$nextTick(() => {
            this.isInitializing = false;
          });
        },
      },
      rescheduleStartDate(startDateTime) {
        if (!startDateTime || this.isInitializing) return;

        const endTime = moment(startDateTime).add(1, 'hour');
        const endOfDay = moment(startDateTime).endOf('day');
        this.rescheduleEndDate = (
          endTime.isAfter(endOfDay) ? endOfDay : endTime
        ).format(DateFormats.ISO_DATETIME);
      },
    },
    methods: {
      resetForm() {
        this.isFormValid = false;
        this.rescheduleStartDate = null;
        this.rescheduleEndDate = null;
        this.rescheduleLocation = null;
      },
      formatTime(date) {
        if (!date) return '';
        return moment(date).format(DateFormats.DISPLAY_TIME_AMPM_LOWER);
      },
      formatDateTime(date) {
        if (!date) return '';
        return moment(date).format('ddd D [de] MMM [del] YYYY [a las] hh:mm a');
      },
      onReschedule() {
        if (isConfirmationWindowExpired(this.rescheduleEndDate)) {
          this.$toast.error(
            'No puedes reprogramar a un horario cuyo tiempo de confirmación ya expiró'
          );
          return;
        }

        this.$emit('onReschedule', {
          startDate: this.rescheduleStartDate,
          endDate: this.rescheduleEndDate,
          location: this.rescheduleLocation,
        });
      },
    },
  };
</script>

<style scoped>
  ::v-deep .vc-container {
    border: none;
    width: 100%;
  }

  ::v-deep .v-list-item__title,
  .v-list-item__subtitle {
    text-align: left;
  }
</style>
